import { useEffect } from 'react';
import { ClickAwayListener, IconButton, Paper, Popper, PopperProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { SettlementLoadRecord } from '@api/Trinium/Endpoints/Settlements/types';
import { GenericOverlay } from '@components/DataGrid/GenericNoRowsOverlay';
import { columns } from '../Loads/types';
import { settlementsGridSx, settlementsRowHeight } from '../gridStyles';

import styles from './LoadsByServicePopper.module.css';

/** Code and Service are constant across the drill-down, and Notes is too wide for an anchored popup. */
const OMITTED_FIELDS = new Set(['serviceCode', 'serviceDescription', 'notes']);
const drilldownColumns = columns.filter((column) => !OMITTED_FIELDS.has(column.field));

/** The grid multiplies both heights by the density factor, so size the popup off the rendered values. */
const COMPACT_DENSITY_FACTOR = 0.7;
const COLUMN_HEADER_HEIGHT = 56;
const renderedRowHeight = Math.floor(settlementsRowHeight * COMPACT_DENSITY_FACTOR);
const renderedHeaderHeight = Math.floor(COLUMN_HEADER_HEIGHT * COMPACT_DENSITY_FACTOR);
const MIN_VISIBLE_ROWS = 3;
const MAX_VISIBLE_ROWS = 12;

function gridHeight(rowCount: number) {
  const visibleRows = Math.min(Math.max(rowCount, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS);
  // Slack for the grid's bottom border, so an exact fit does not produce a scrollbar.
  return renderedHeaderHeight + visibleRows * renderedRowHeight + 2;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

/** Keeps the popup inside the viewport as its height changes, without detaching from the row. */
const modifiers: NonNullable<PopperProps['modifiers']> = [
  { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'], padding: 8 } },
  { name: 'preventOverflow', options: { padding: 8 } }
];

const gridSlots = {
  noRowsOverlay: () => <GenericOverlay overlayText='No Loads Found For This Service.' />
};

/** One Load Commissions Breakdown line: a service's load rows and their commission total. */
export interface LoadCommissionLine {
  serviceCode: string;
  serviceDescription: string;
  amount: number;
  loads: SettlementLoadRecord[];
}

export interface LoadsByServiceTarget {
  anchorEl: HTMLElement;
  line: LoadCommissionLine;
}

/** Drill-down for a Load Commissions Breakdown row: the Loads-tab rows that make up that line's total. */
export default function LoadsByServicePopper({ target, onClose }: { target: LoadsByServiceTarget | null; onClose: () => void }) {
  useEffect(() => {
    if (target === null) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [target, onClose]);

  if (target === null) {
    return null;
  }

  const { line } = target;

  return (
    <Popper
      open
      anchorEl={target.anchorEl}
      placement='bottom-start'
      modifiers={modifiers}
      className={styles.popper}>
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          className={styles.paper}>
          <div className={styles.header}>
            <span className={styles.title}>{line.serviceDescription}</span>
            <span className={styles.meta}>{`${String(line.loads.length)} loads · ${currencyFormatter.format(line.amount)}`}</span>
            <IconButton
              aria-label='close'
              onClick={onClose}
              size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </div>

          <div style={{ height: gridHeight(line.loads.length) }}>
            <DataGridPro
              columns={drilldownColumns}
              rows={line.loads}
              density='compact'
              rowHeight={settlementsRowHeight}
              columnHeaderHeight={COLUMN_HEADER_HEIGHT}
              hideFooter
              // The theme turns the toolbar on for every grid; the popup's own header covers it.
              showToolbar={false}
              disableColumnMenu
              disableRowSelectionOnClick
              slots={gridSlots}
              sx={settlementsGridSx}
            />
          </div>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
